import { useCallback, useEffect, useRef, useState } from 'react';
import { Bug, Heart, Lightbulb, MessageSquare, Paperclip, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  type FeedbackCategory,
  submitFeedback,
  uploadScreenshot,
} from '@/lib/feedback';

/**
 * In-app feedback capture. See `docs/product/feedback-proposal.md`.
 *
 * Two trigger surfaces share this modal:
 *   - UserMenu dropdown → `defaultCategory='other'`
 *   - Editor floating button → `defaultCategory='bug'`
 *
 * Screenshot UX: paste / drag / file-picker. We **don't** do automatic
 * `html2canvas` capture — too unreliable inside the GrapesJS editor and
 * across cross-origin images. User-initiated only.
 */

export interface FeedbackModalProps {
  open: boolean;
  defaultCategory?: FeedbackCategory;
  onClose: () => void;
}

const CATEGORIES: Array<{
  id: FeedbackCategory;
  label: string;
  icon: typeof Bug;
}> = [
  { id: 'bug', label: 'Bug', icon: Bug },
  { id: 'idea', label: 'Idea', icon: Lightbulb },
  { id: 'other', label: 'Other', icon: MessageSquare },
  { id: 'love', label: 'Love', icon: Heart },
];

const MAX_MESSAGE = 2000;
const MIN_MESSAGE = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

export function FeedbackModal({
  open,
  defaultCategory = 'other',
  onClose,
}: FeedbackModalProps) {
  const [category, setCategory] = useState<FeedbackCategory>(defaultCategory);
  const [message, setMessage] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setCategory(defaultCategory);
      setMessage('');
      setScreenshotFile(null);
      setScreenshotPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setSubmitting(false);
      setError(null);
      setDone(false);
    }
  }, [open, defaultCategory]);

  // Free object URL on unmount
  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  const handleFile = useCallback((file: File | null) => {
    setError(null);
    if (!file) {
      setScreenshotFile(null);
      setScreenshotPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    if (!ALLOWED_FILE_TYPES.has(file.type)) {
      setError('Screenshot must be PNG, JPG, WebP or GIF.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('Screenshot too large (max 5 MB).');
      return;
    }
    setScreenshotFile(file);
    setScreenshotPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  // Paste-from-clipboard handler — only fires while the modal is open.
  useEffect(() => {
    if (!open) return;
    function onPaste(e: ClipboardEvent) {
      if (!e.clipboardData) return;
      for (const item of e.clipboardData.items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            handleFile(file);
            e.preventDefault();
            return;
          }
        }
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, handleFile]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = message.trim();
    if (submitting) return;
    if (trimmed.length < MIN_MESSAGE) {
      setError(`Please write at least ${MIN_MESSAGE} characters.`);
      return;
    }
    if (trimmed.length > MAX_MESSAGE) {
      setError(`Please keep it under ${MAX_MESSAGE} characters.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let screenshotKey: string | null = null;
      if (screenshotFile) {
        const uploaded = await uploadScreenshot(screenshotFile, screenshotFile.name);
        screenshotKey = uploaded.key;
      }
      await submitFeedback({ category, message: trimmed, screenshotKey });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  }

  const charCount = message.length;
  const overLimit = charCount > MAX_MESSAGE;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onClose()}>
      <DialogContent className="max-h-[90vh] gap-5 overflow-x-hidden overflow-y-auto rounded-2xl sm:max-w-md">
        {done ? (
          <SuccessView onClose={onClose} />
        ) : (
          <>
            <DialogHeader className="min-w-0 text-left">
              <DialogTitle>Tell us what's on your mind</DialogTitle>
              <DialogDescription>
                Bugs, ideas, anything — we read every message.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="min-w-0 space-y-4">
              <fieldset>
                <Label className="text-muted-foreground text-xs">Type</Label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const active = category === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setCategory(c.id)}
                        aria-pressed={active}
                        className={cn(
                          'flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors',
                          active
                            ? 'border-primary/40 bg-accent text-foreground'
                            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Icon className="size-4" aria-hidden />
                        <span>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="feedback-message" className="text-muted-foreground text-xs">
                    Message
                  </Label>
                  <span
                    className={cn(
                      'text-[10px] tabular-nums',
                      overLimit ? 'text-destructive font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {charCount} / {MAX_MESSAGE}
                  </span>
                </div>
                <Textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={placeholderFor(category)}
                  disabled={submitting}
                  rows={5}
                  className="resize-none"
                  autoFocus
                />
              </div>

              <ScreenshotZone
                file={screenshotFile}
                previewUrl={screenshotPreview}
                onPick={(file) => handleFile(file)}
                onClear={() => handleFile(null)}
                onClickPicker={() => fileInputRef.current?.click()}
                disabled={submitting}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />

              <p className="text-muted-foreground text-[11px] leading-relaxed">
                We'll also include your email, current page, plan tier, and browser
                size to help us reproduce issues.
              </p>

              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || message.trim().length < MIN_MESSAGE}>
                  {submitting ? 'Sending…' : 'Send feedback'}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuccessView({ onClose }: { onClose: () => void }) {
  return (
    <div className="text-center pt-2">
      <div className="bg-success/15 text-success mx-auto grid size-12 place-items-center rounded-full text-xl">
        ✓
      </div>
      <DialogHeader className="mt-4">
        <DialogTitle>Thanks for the feedback</DialogTitle>
        <DialogDescription className="text-muted-foreground">
          We read every message. If we need to follow up, we'll email you.
        </DialogDescription>
      </DialogHeader>
      <Button className="mt-6 mx-auto w-full max-w-[200px]" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

function ScreenshotZone({
  file,
  previewUrl,
  onPick,
  onClear,
  onClickPicker,
  disabled,
}: {
  file: File | null;
  previewUrl: string | null;
  onPick: (file: File) => void;
  onClear: () => void;
  onClickPicker: () => void;
  disabled: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onPick(dropped);
  }

  if (file && previewUrl) {
    return (
      <div className="border-border bg-muted/40 flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-md border p-2">
        <img
          src={previewUrl}
          alt="Screenshot preview"
          className="h-12 w-12 shrink-0 rounded object-cover"
        />
        <div className="min-w-0 flex-1 overflow-hidden">
          <p className="truncate text-sm" title={file.name || 'screenshot'}>
            {file.name || 'screenshot'}
          </p>
          <p className="text-muted-foreground truncate text-xs">
            {formatBytes(file.size)} · {file.type.replace('image/', '').toUpperCase()}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClear}
          disabled={disabled}
          aria-label="Remove screenshot"
          className="shrink-0"
        >
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClickPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickPicker();
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed p-4 text-xs transition-colors outline-none',
        dragOver
          ? 'border-primary/50 bg-accent/40'
          : 'border-border text-muted-foreground hover:bg-muted/50',
      )}
    >
      <Paperclip className="size-4" aria-hidden />
      <span className="font-medium">Add screenshot (optional)</span>
      <span className="text-[10px]">Paste, drop, or click to pick · PNG/JPG · up to 5 MB</span>
    </div>
  );
}

function placeholderFor(category: FeedbackCategory): string {
  switch (category) {
    case 'bug':
      return "What happened? What did you expect to happen?";
    case 'idea':
      return "What would help? Who is it for?";
    case 'love':
      return "What's working well for you?";
    case 'other':
    default:
      return "Anything on your mind...";
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
